import { HelpOutline } from "@mui/icons-material";
import { Tooltip } from "@mui/material";
import { useMemo, useState } from "react";
import BaseTable from "../common/BaseTable";
import { useMarkdownRenderer } from "../hooks/useMarkdownRenderer";
import { useCustomizeSpecData } from "../hooks/useTableData";
import type { CustomizeSpecDetail, CustomizeSpecTableProps } from "../types";
import styles from "./index.module.scss";

const SUPPORT_STATUS_TOOLTIP = `【対応】
kintone内で完結するカスタマイズ。コード自動生成可能。

【対象外】
外部API連携（kintone.proxy()等）やREQXIGNだけでは実現できない処理。コード生成不可。`;

const TABLE_HEADERS = ["機能名", "適用アプリ名", "概要", "処理条件"];

const DETAIL_FIELDS = [
  "機能名",
  "適用アプリ名",
  "概要",
  "入力フィールド",
  "出力フィールド",
  "処理条件",
  "機能詳細",
  "設定情報",
  "イベント契機",
  "対応端末",
  "対応区分",
];

const CustomizeSpecTable: React.FC<CustomizeSpecTableProps> = ({
  formData,
  content,
  mode,
  maxRows = 4,
  className = "",
}) => {
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);
  const {
    parsed: parsedContent,
    isMarkdown,
    isUnknownJson,
    content: rawContent,
  } = useCustomizeSpecData(formData, content);
  const { renderMarkdown } = useMarkdownRenderer();

  const { displayHeaders, tableRows } = useMemo(() => {
    if (!parsedContent?.headers || !parsedContent?.rows) {
      return { displayHeaders: [], tableRows: [] };
    }

    const tableHeaderIndices = TABLE_HEADERS.map((header) => parsedContent.headers.indexOf(header));

    const tableRows = parsedContent.rows.map((row) =>
      TABLE_HEADERS.map((_, idx) => {
        const headerIndex = tableHeaderIndices[idx];
        return headerIndex !== undefined && headerIndex >= 0 ? (row[headerIndex] ?? "") : "";
      }),
    );

    return { displayHeaders: TABLE_HEADERS, tableRows };
  }, [parsedContent]);

  const selectedRowDetail = useMemo<CustomizeSpecDetail | null>(() => {
    if (selectedRowIndex === null || !parsedContent?.rows || !parsedContent?.headers) {
      return null;
    }

    const row = parsedContent.rows[selectedRowIndex];
    if (!row) return null;

    const detailObj: CustomizeSpecDetail = {
      機能名: "",
      適用アプリ名: "",
      概要: "",
      入力フィールド: "",
      出力フィールド: "",
      処理条件: "",
      機能詳細: "",
      設定情報: "",
      イベント契機: "",
      対応端末: "",
      対応区分: "",
    };

    DETAIL_FIELDS.forEach((field) => {
      const index = parsedContent.headers.indexOf(field);
      detailObj[field] = index >= 0 ? (row[index] ?? "") : "";
    });

    return detailObj;
  }, [selectedRowIndex, parsedContent]);

  // JSONだが期待する形式でない場合は整形して表示
  if (isUnknownJson) {
    try {
      const formatted = JSON.stringify(JSON.parse(rawContent), null, 2);
      return (
        <div
          className={`${styles["wrapper"]} ${mode === "compact" ? styles["wrapper--compact"] : ""} ${className}`}
        >
          <pre className={styles["jsonPreview"]}>
            {mode === "compact" ? `${formatted.slice(0, 300)}...` : formatted}
          </pre>
        </div>
      );
    } catch {
      return <div className={styles["empty"]}>データがありません</div>;
    }
  }

  if (isMarkdown) {
    // 空のコンテンツの場合は「データがありません」を表示
    if (!rawContent || rawContent.trim() === "") {
      return <div className={styles["empty"]}>データがありません</div>;
    }
    // compactモードでは重いMarkdownレンダリングを避けてテキスト表示
    if (mode === "compact") {
      const truncated = rawContent.substring(0, 300);
      return (
        <div className={`${styles["wrapper"]} ${styles["wrapper--compact"]} ${className}`}>
          <pre className={styles["markdownPreviewText"]}>{truncated}...</pre>
        </div>
      );
    }
    return (
      <div className={`${styles["wrapper"]} ${className}`}>
        <div className={styles["markdownContent"]}>{renderMarkdown(rawContent)}</div>
      </div>
    );
  }

  if (!parsedContent) {
    return <div className={styles["empty"]}>データがありません</div>;
  }

  return (
    <div
      className={`${styles["wrapper"]} ${mode === "compact" ? styles["wrapper--compact"] : ""} ${className}`}
    >
      <BaseTable
        mode={mode}
        headers={displayHeaders}
        rows={tableRows}
        maxRows={mode === "compact" ? maxRows : undefined}
        onRowClick={mode === "full" ? setSelectedRowIndex : undefined}
        selectedRowIndex={mode === "full" ? selectedRowIndex : undefined}
      />

      {mode === "full" && selectedRowDetail && (
        <div className={styles["detailSection"]}>
          <h2 className={styles["detailTitle"]}>{selectedRowDetail["機能名"]}</h2>

          <div className={styles["detailContainer"]}>
            <div className={styles["detailItem"]}>
              <div className={styles["detailItemContent"]}>
                {renderMarkdown(selectedRowDetail["概要"] || "")}
              </div>
            </div>

            <div className={styles["detailItem"]}>
              <h2 className={styles["sectionHeading"]}>機能詳細</h2>
              <div className={styles["detailItemContent"]}>
                {renderMarkdown(selectedRowDetail["機能詳細"] || "")}
              </div>
            </div>

            <div className={styles["detailItem"]}>
              <h2 className={styles["sectionHeading"]}>入力・出力情報</h2>
              {[
                { key: "入力フィールド", label: "入力フィールド" },
                { key: "出力フィールド", label: "出力フィールド" },
                { key: "イベント契機", label: "イベント契機" },
                { key: "処理条件", label: "処理条件" },
              ].map(
                ({ key, label }) =>
                  selectedRowDetail[key] && (
                    <div key={key} className={styles["tableRow"]}>
                      <div className={styles["tableHeader"]}>{label}</div>
                      <div className={styles["tableContent"]}>
                        <div className={styles["detailItemContent"]}>
                          {renderMarkdown(selectedRowDetail[key] || "")}
                        </div>
                      </div>
                    </div>
                  ),
              )}
            </div>

            <div className={styles["detailItem"]}>
              <h2 className={styles["sectionHeading"]}>動作環境・設定</h2>
              {[
                { key: "適用アプリ名", label: "適用アプリ名" },
                { key: "設定情報", label: "設定情報" },
                { key: "対応端末", label: "対応端末" },
              ].map(
                ({ key, label }) =>
                  selectedRowDetail[key] && (
                    <div key={key} className={styles["tableRow"]}>
                      <div className={styles["tableHeader"]}>{label}</div>
                      <div className={styles["tableContent"]}>
                        <div className={styles["detailItemContent"]}>
                          {renderMarkdown(selectedRowDetail[key] || "")}
                        </div>
                      </div>
                    </div>
                  ),
              )}
            </div>

            {selectedRowDetail["対応区分"] && (
              <div className={styles["detailItem"]}>
                <h2 className={styles["sectionHeading"]}>
                  対応区分
                  <Tooltip
                    title={<span style={{ whiteSpace: "pre-line" }}>{SUPPORT_STATUS_TOOLTIP}</span>}
                    placement="right"
                    arrow
                  >
                    <HelpOutline className={styles["helpIcon"] ?? ""} />
                  </Tooltip>
                </h2>
                <div className={styles["tableRow"]}>
                  <div className={styles["tableHeader"]}>対応区分</div>
                  <div className={styles["tableContent"]}>
                    <div className={styles["detailItemContent"]}>
                      <span
                        className={`${styles["supportBadge"]} ${
                          selectedRowDetail["対応区分"] === "対応"
                            ? styles["supportBadge--supported"]
                            : selectedRowDetail["対応区分"] === "対象外"
                              ? styles["supportBadge--unsupported"]
                              : ""
                        }`}
                      >
                        {selectedRowDetail["対応区分"]}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomizeSpecTable;
